import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import clsx from "clsx";
import styles from "./CompassLanding.module.css";
import {
  HeroSection,
  HowItWorksSection,
  FeaturesSection,
  MakerNoteSection,
  PricingSection,
  FAQSection,
  CTASection,
} from "../components/sections/Compass";
import { AppleIcon } from "../components/sections/Compass/AppStoreBadge";
import { navigationLinks, heroData } from "../data/compassData";

const LogoIcon = () => (
  <img src="/images/compass/logo.png" alt="" aria-hidden="true" className={styles.logoIcon} />
);

export const CompassLanding: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Static head tags for /compass come from build/compass.html (scripts/route-heads.js);
  // this covers in-app navigation to the page.
  useEffect(() => {
    const previous = document.title;
    document.title = "Compass: a habit tracker built around your values";
    return () => {
      document.title = previous;
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 24);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // The sections render after the browser tries to honor a #hash on load, so
  // scroll to it once they exist (e.g. a shared /compass#pricing link), and
  // again after web fonts swap in and shift the layout.
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (!id) return;
    // "instant" overrides the global smooth scrolling; TS 4.9's DOM lib predates it.
    const scrollToHash = () =>
      document.getElementById(id)?.scrollIntoView({ behavior: "instant" as ScrollBehavior });
    scrollToHash();
    document.fonts?.ready.then(scrollToHash);
  }, []);

  useEffect(() => {
    if (!isMobileMenuOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsMobileMenuOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isMobileMenuOpen]);

  const closeMenu = () => setIsMobileMenuOpen(false);

  return (
    <div className={styles.page}>
      <a href="#main-content" className={styles.skipLink}>
        Skip to main content
      </a>

      <nav
        className={clsx(styles.nav, (isScrolled || isMobileMenuOpen) && styles.navScrolled)}
        aria-label="Compass"
      >
        <div className={styles.navContainer}>
          <Link to="/compass" className={styles.logo}>
            <LogoIcon />
            <span>Compass</span>
          </Link>

          <ul className={styles.navLinks}>
            {navigationLinks.map((link) => (
              <li key={link.id}>
                <a href={`#${link.id}`} className={styles.navLink}>
                  {link.label}
                </a>
              </li>
            ))}
          </ul>

          <a
            href={heroData.appStoreUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.navCta}
            aria-label="Download Compass on the App Store (opens in a new tab)"
          >
            <AppleIcon className={styles.appStoreIconSmall} />
            <span>Get the app</span>
          </a>

          <button
            className={styles.mobileMenuButton}
            onClick={() => setIsMobileMenuOpen((open) => !open)}
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMobileMenuOpen}
            aria-controls="compass-mobile-menu"
          >
            <span className={clsx(styles.hamburger, isMobileMenuOpen && styles.hamburgerOpen)} />
          </button>
        </div>

        <div
          id="compass-mobile-menu"
          className={clsx(styles.mobileMenu, isMobileMenuOpen && styles.mobileMenuOpen)}
        >
          <ul className={styles.mobileNavLinks}>
            {navigationLinks.map((link) => (
              <li key={link.id}>
                <a href={`#${link.id}`} className={styles.mobileNavLink} onClick={closeMenu}>
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </nav>

      <main id="main-content" tabIndex={-1}>
        <HeroSection />
        <HowItWorksSection />
        <FeaturesSection />
        <MakerNoteSection />
        <PricingSection />
        <FAQSection />
        <CTASection />
      </main>

      <footer className={styles.footer}>
        <div className={styles.footerContainer}>
          <div>
            <Link to="/compass" className={styles.footerLogo}>
              <LogoIcon />
              <span>Compass</span>
            </Link>
            <p className={styles.footerTagline}>
              A habit tracker built around what matters to you. Made by an independent developer.
            </p>
          </div>

          <div className={styles.footerLinks}>
            <div className={styles.footerColumn}>
              <h2 className={styles.footerHeading}>Product</h2>
              <ul>
                {navigationLinks.map((link) => (
                  <li key={link.id}>
                    <a href={`#${link.id}`}>{link.label}</a>
                  </li>
                ))}
              </ul>
            </div>
            <div className={styles.footerColumn}>
              <h2 className={styles.footerHeading}>Legal</h2>
              <ul>
                <li>
                  <Link to="/compass/privacy">Privacy Policy</Link>
                </li>
                <li>
                  <Link to="/compass/terms">Terms of Service</Link>
                </li>
              </ul>
            </div>
            <div className={styles.footerColumn}>
              <h2 className={styles.footerHeading}>Support</h2>
              <ul>
                <li>
                  <a href="mailto:dixonappdevelopment@gmail.com">dixonappdevelopment@gmail.com</a>
                </li>
                <li>
                  <Link to="/compass/feedback">Feature requests</Link>
                </li>
                <li>
                  <Link to="/">Dixon App Development</Link>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className={styles.footerBottom}>
          <p>&copy; {new Date().getFullYear()} Dixon App Development</p>
        </div>
      </footer>
    </div>
  );
};
